import { forwardRef } from 'react'

interface Option {
  label: string
  value: string | number
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  options: Option[]
  placeholder?: string
  error?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, placeholder = 'Select an option', error, className = '', disabled, ...props }, ref) => {
    const hasError = !!error

    return (
      <div className={`flex flex-col w-full ${label ? 'mb-5' : ''}`}>
        {label && (
          <label
            className={`
              text-xs font-semibold uppercase tracking-wider mb-2
              ${hasError ? 'text-error' : 'text-primary'}
            `}
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          disabled={disabled}
          className={`
            w-full px-4 py-3 pr-10 border-2 rounded-lg text-base outline-none bg-white
            cursor-pointer appearance-none transition-all duration-200
            bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23f57b20' d='M6 8L1 3h10z'/%3E%3C/svg%3E")]
            bg-no-repeat bg-[right_16px_center]
            ${
              hasError
                ? 'border-error focus:border-error focus:shadow-[0_0_0_3px_rgba(255,123,92,0.1)]'
                : 'border-gray-lighter focus:border-primary focus:shadow-[0_0_0_3px_rgba(230, 83, 0,0.1)] hover:border-gray'
            }
            ${disabled ? 'bg-gray-lighter cursor-not-allowed text-gray-light' : ''}
            ${className}
          `}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <span className="text-xs text-error mt-1.5 flex items-center gap-1">{error}</span>}
      </div>
    )
  }
)

Select.displayName = 'Select'
